/**
 * Dictionnaire i18n du formulaire de réservation (FR/EN).
 *
 * IMPORTANT : ce module ne contient QUE des libellés d'affichage + messages de
 * validation. La logique (machine à étapes, conditions de validation, calcul de
 * prix, soumission) n'est PAS touchée — seuls les textes deviennent localisés.
 * Défaut `fr` → zéro régression. La soumission/email est déjà locale-aware
 * (useReservationForm reçoit `locale`).
 */
import type { Locale } from "@/config/i18n";
import { type I18n } from "@/config/localeText";
import { fr, enUS } from "date-fns/locale";

export const dateFnsLocale = (locale: Locale) => (locale === "en" ? enUS : fr);

export const WEEK_DAYS_BY_LOCALE: I18n<string[]> = {
  fr: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  ar: ["إثن", "ثلا", "أرب", "خمي", "جمع", "سبت", "أحد"],
};

export type ReservationUi = {
  hero: { h1: string; subtitle: string; customizeAria: string };
  page: {
    crumbHome: string;
    whyHeading: string;
    why: { title: string; text: string }[];
    faqHeading: string;
    faq: { q: string; a: string }[];
    inspirationHeading: string;
    links: { label: string; href: string }[];
    invoiceSubject: string;
    invoiceReviewSubject: string;
    seo: { title: string; description: string; canonical: string };
  };
  step1: {
    tripTypeHeading: string;
    oneWay: string;
    roundTrip: string;
    multiTrip: string;
    disposition: string;
    dispositionDuration: string;
    minHours: string;
    reduceDuration: string;
    increaseDuration: string;
    differentReturn: string;
    tripDetailsHeading: string;
    departureLabel: string;
    departurePlaceholder: string;
    arrivalLabel: string;
    arrivalPlaceholder: string;
    destination: string;
    anywhere: string;
    dateLabel: string;
    timeLabel: string;
    returnDateLabel: string;
    returnTimeLabel: string;
    returnAddressLabel: string;
    returnAddressPlaceholder: string;
    stepLabel: (n: number) => string;
    stopPlaceholder: string;
    waitingTime: string;
    min: string;
    removeStop: string;
    addStop: (n: number, max: number) => string;
    finalArrivalLabel: string;
    finalArrivalPlaceholder: string;
    vehiclePassengersHeading: string;
    adults: string;
    adultsAge: string;
    reduceAdults: string;
    increaseAdults: string;
    showAdvanced: string;
    hideAdvanced: string;
    children: string;
    childrenAge: string;
    reduceChildren: string;
    increaseChildren: string;
    babies: string;
    babiesAge: string;
    reduceBabies: string;
    increaseBabies: string;
    luggage: string;
    luggageDesc: string;
    reduceLuggage: string;
    increaseLuggage: string;
    estimatePrice: string;
    reserve: string;
    v: {
      departure: string;
      date: string;
      time: string;
      arrival: string;
      returnDate: string;
      returnTime: string;
    };
    estimateError: string;
    priceCalcError: string;
    reservationError: string;
    unexpectedError: string;
    modalTitle: string;
    modalBody: string;
    cancel: string;
    continueOneWay: string;
  };
  step2: {
    back: string;
    yourInfo: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    phone: string;
    country: string;
    expectedFormat: (example: string) => string;
    email: string;
    emailPlaceholder: string;
    company: string;
    companyPlaceholder: string;
    sending: string;
    confirm: string;
    v: {
      firstNameRequired: string;
      firstNameMin: string;
      lastNameRequired: string;
      lastNameMin: string;
      phoneRequired: string;
      phoneInvalid: (example: string) => string;
      emailRequired: string;
      emailInvalid: string;
    };
  };
  datePicker: {
    today: string;
    selectDate: string;
    prev: string;
    next: string;
  };
  timePicker: {
    now: string;
    selectTime: string;
    hours: string;
    minutes: string;
    hoursTitle: (h: string) => string;
    minutesTitle: (m: string) => string;
  };
  addressInput: {
    stations: string;
    airports: string;
    destinations: string;
    hotels: string;
    flightNumber: string;
    flightExample: string;
    trainNumber: string;
    trainExample: string;
  };
  suggestions: {
    gare: string;
    aeroport: string;
    destination: string;
    hotel: string;
    poi: string;
    none: string;
  };
  vehicle: {
    select: string;
    upToPassengers: (n: number) => string;
    upToLuggage: (n: number) => string;
    fromPerKm: (price: number) => string;
    tooManyPassengers: (a: number, b: number) => string;
    tooManyLuggage: (a: number, b: number) => string;
  };
  /** Libellés propres au formulaire express (ReservationFormMinimal). */
  minimal: {
    title: string;
    advancedOptions: string;
    departureLabel: string;
    departurePlaceholder: string;
    arrivalLabel: string;
    arrivalPlaceholder: string;
    dateLabel: string;
    timeLabel: string;
    reducePassengers: string;
    increasePassengers: string;
    estimatePrice: string;
    reserve: string;
    defaultError: string;
    successTitle: string;
    successReceived: string;
    successContactCustom: string;
    successContactStandard: string;
    newReservation: string;
  };
};

export const RESERVATION_UI: I18n<ReservationUi> = {
  fr: {
    hero: {
      h1: "Réservation haut de gamme",
      subtitle:
        "Déplacements privés, transferts et mises à disposition avec KS Rent Services.",
      customizeAria: "Personnaliser votre trajet avec options supplémentaires",
    },
    page: {
      crumbHome: "Accueil",
      whyHeading: "Pourquoi réserver avec KS Rent Services",
      why: [
        {
          title: "Processus rapide",
          text: "Demande, validation et confirmation en quelques étapes avec suivi clair.",
        },
        {
          title: "Tarification lisible",
          text: "Conditions explicites avant validation, sans ambiguïté sur la prestation.",
        },
        {
          title: "Équipe locale",
          text: "Accompagnement opérationnel sur Cannes, Nice, Monaco et la Côte d'Azur.",
        },
      ],
      faqHeading: "FAQ réservation",
      faq: [
        {
          q: "Sous quel délai ai-je une confirmation ?",
          a: "Notre équipe répond rapidement avec une confirmation détaillée selon votre demande.",
        },
        {
          q: "Puis-je modifier ma reservation ?",
          a: "Oui, selon disponibilités, nous adaptons le service à vos contraintes de timing.",
        },
        {
          q: "Chauffeur et location sont-ils combinables ?",
          a: "Oui, nous proposons des combinaisons véhicule + chauffeur selon votre contexte.",
        },
      ],
      inspirationHeading: "Besoin d'inspiration avant réservation ?",
      links: [
        { label: "Services chauffeur privé", href: "/chauffeur/services" },
        { label: "Excursions depuis Cannes", href: "/excursions" },
        { label: "Location de véhicules premium", href: "/location/vehicules" },
      ],
      invoiceSubject: "Demande de facture",
      invoiceReviewSubject: "Demande facture + avis",
      seo: {
        title: "Réservation chauffeur privé & VTC | KS Rent Services",
        description:
          "Réservez votre chauffeur privé, transfert ou mise à disposition à Cannes et sur la Côte d'Azur. Estimation de prix immédiate, confirmation rapide.",
        canonical: "/reservation",
      },
    },
    step1: {
      tripTypeHeading: "Type de Trajet",
      oneWay: "Aller simple",
      roundTrip: "Aller-retour",
      multiTrip: "Multi-trajets",
      disposition: "Mise à disposition",
      dispositionDuration: "Durée de mise à disposition",
      minHours: "Minimum 3 heures",
      reduceDuration: "Réduire la durée",
      increaseDuration: "Augmenter la durée",
      differentReturn: "Adresse de retour différente",
      tripDetailsHeading: "Détails du Trajet",
      departureLabel: "Lieu de départ",
      departurePlaceholder: "Saisissez votre adresse",
      arrivalLabel: "Lieu d'arrivée",
      arrivalPlaceholder: "Saisissez votre destination",
      destination: "Destination",
      anywhere: "Nous irons où vous voulez !",
      dateLabel: "Date de départ",
      timeLabel: "Heure de départ",
      returnDateLabel: "Date de retour",
      returnTimeLabel: "Heure de retour",
      returnAddressLabel: "Adresse de retour",
      returnAddressPlaceholder: "Saisissez l'adresse de retour",
      stepLabel: (n) => `Étape ${n}`,
      stopPlaceholder: "Saisissez l'adresse de l'étape",
      waitingTime: "Temps d'attente",
      min: "min",
      removeStop: "Supprimer l'étape",
      addStop: (n, max) => `Ajouter une étape (${n}/${max})`,
      finalArrivalLabel: "Lieu d'arrivée final",
      finalArrivalPlaceholder: "Saisissez votre destination finale",
      vehiclePassengersHeading: "Véhicule et Passagers",
      adults: "Adultes",
      adultsAge: "12 ans et plus",
      reduceAdults: "Réduire le nombre d'adultes",
      increaseAdults: "Augmenter le nombre d'adultes",
      showAdvanced: "Afficher les options avancées (enfants, bébés, bagages)",
      hideAdvanced: "Masquer les options avancées",
      children: "Enfants",
      childrenAge: "De 3 à 11 ans",
      reduceChildren: "Réduire le nombre de sièges enfant",
      increaseChildren: "Augmenter le nombre de sièges enfant",
      babies: "Bébés",
      babiesAge: "Moins de 3 ans",
      reduceBabies: "Réduire le nombre de sièges bébé",
      increaseBabies: "Augmenter le nombre de sièges bébé",
      luggage: "Bagages",
      luggageDesc: "Valises standard",
      reduceLuggage: "Réduire le nombre de bagages",
      increaseLuggage: "Augmenter le nombre de bagages",
      estimatePrice: "Estimer le prix",
      reserve: "Réserver",
      v: {
        departure: "Le lieu de départ est requis",
        date: "La date est requise",
        time: "L'heure est requise",
        arrival: "Le lieu d'arrivée est requis",
        returnDate: "La date de retour est requise",
        returnTime: "L'heure de retour est requise",
      },
      estimateError:
        "Impossible d'estimer le prix avec les informations actuelles.",
      priceCalcError:
        "Une erreur est survenue lors du calcul du prix. Veuillez réessayer.",
      reservationError: "Une erreur est survenue lors de la réservation",
      unexpectedError:
        "Une erreur inattendue est survenue. Veuillez réessayer.",
      modalTitle: "Aucune étape ajoutée",
      modalBody:
        "Vous n'avez pas ajouté d'étapes à votre trajet. Souhaitez-vous continuer en mode \"aller simple\" ?",
      cancel: "Annuler",
      continueOneWay: "Continuer en aller simple",
    },
    step2: {
      back: "Retour",
      yourInfo: "Vos informations",
      firstName: "Prénom",
      firstNamePlaceholder: "Votre prénom",
      lastName: "Nom",
      lastNamePlaceholder: "Votre nom",
      phone: "Téléphone",
      country: "Pays",
      expectedFormat: (example) => `Format attendu : ${example}`,
      email: "Email",
      emailPlaceholder: "votre@email.com",
      company: "Entreprise (facultatif)",
      companyPlaceholder: "Nom de votre entreprise",
      sending: "Envoi en cours...",
      confirm: "Confirmer la réservation",
      v: {
        firstNameRequired: "Le prénom est requis",
        firstNameMin: "Le prénom doit contenir au moins 2 caractères",
        lastNameRequired: "Le nom est requis",
        lastNameMin: "Le nom doit contenir au moins 2 caractères",
        phoneRequired: "Le numéro de téléphone est requis",
        phoneInvalid: (example) => `Format invalide. Exemple : ${example}`,
        emailRequired: "L'email est requis",
        emailInvalid: "L'email n'est pas valide",
      },
    },
    datePicker: {
      today: "Aujourd'hui",
      selectDate: "Sélectionner une date",
      prev: "Précédent",
      next: "Suivant",
    },
    timePicker: {
      now: "Maintenant",
      selectTime: "Sélectionner une heure",
      hours: "Heures",
      minutes: "Minutes",
      hoursTitle: (h) => `${h} heures`,
      minutesTitle: (m) => `${m} minutes`,
    },
    addressInput: {
      stations: "Gares",
      airports: "Aéroports",
      destinations: "Destinations",
      hotels: "Hôtels",
      flightNumber: "Numéro de vol (facultatif)",
      flightExample: "Ex: AF1234",
      trainNumber: "Numéro de train (facultatif)",
      trainExample: "Ex: TGV 6214",
    },
    suggestions: {
      gare: "Gare",
      aeroport: "Aéroport",
      destination: "Destination",
      hotel: "Hôtel",
      poi: "POI",
      none: "Aucune suggestion trouvée",
    },
    vehicle: {
      select: "Sélectionnez votre véhicule",
      upToPassengers: (n) => `Jusqu'à ${n} passagers`,
      upToLuggage: (n) => `Jusqu'à ${n} bagages`,
      fromPerKm: (p) => `dès ${p.toFixed(2).replace(".", ",")} €/km`,
      tooManyPassengers: (a, b) => `Trop de passagers (${a} / ${b})`,
      tooManyLuggage: (a, b) => `Trop de bagages (${a} / ${b})`,
    },
    minimal: {
      title: "Réservation Express",
      advancedOptions: "Options avancées",
      departureLabel: "Lieu de départ",
      departurePlaceholder: "Saisissez votre adresse",
      arrivalLabel: "Lieu d'arrivée",
      arrivalPlaceholder: "Saisissez votre destination",
      dateLabel: "Date",
      timeLabel: "Heure",
      reducePassengers: "Réduire le nombre de passagers",
      increasePassengers: "Augmenter le nombre de passagers",
      estimatePrice: "Estimer le prix",
      reserve: "Réserver",
      defaultError: "Une erreur est survenue lors de la réservation",
      successTitle: "Réservation envoyée avec succès !",
      successReceived: "Nous avons bien reçu votre demande de réservation.",
      successContactCustom:
        "Nous vous contacterons dans les 48 heures pour confirmer votre trajet personnalisé.",
      successContactStandard:
        "Nous vous contacterons dans les prochaines heures pour confirmer votre trajet.",
      newReservation: "Nouvelle réservation",
    },
  },
  en: {
    hero: {
      h1: "Premium booking",
      subtitle:
        "Private travel, transfers and hourly hire with KS Rent Services.",
      customizeAria: "Customise your journey with additional options",
    },
    page: {
      crumbHome: "Home",
      whyHeading: "Why book with KS Rent Services",
      why: [
        {
          title: "Fast process",
          text: "Request, validation and confirmation in a few steps with clear tracking.",
        },
        {
          title: "Clear pricing",
          text: "Explicit terms before validation, with no ambiguity about the service.",
        },
        {
          title: "Local team",
          text: "Operational support across Cannes, Nice, Monaco and the French Riviera.",
        },
      ],
      faqHeading: "Booking FAQ",
      faq: [
        {
          q: "How quickly do I get a confirmation?",
          a: "Our team replies quickly with a detailed confirmation based on your request.",
        },
        {
          q: "Can I change my booking?",
          a: "Yes, subject to availability, we adapt the service to your timing constraints.",
        },
        {
          q: "Can chauffeur and rental be combined?",
          a: "Yes, we offer vehicle + chauffeur combinations depending on your context.",
        },
      ],
      inspirationHeading: "Need some inspiration before booking?",
      links: [
        { label: "Private chauffeur services", href: "/en/chauffeur/services" },
        { label: "Excursions from Cannes", href: "/en/excursions" },
        {
          label: "Premium vehicle rental",
          href: "/en/location/vehicules",
        },
      ],
      invoiceSubject: "Invoice request",
      invoiceReviewSubject: "Invoice + review request",
      seo: {
        title: "Private chauffeur & VTC booking | KS Rent Services",
        description:
          "Book your private chauffeur, transfer or hourly hire in Cannes and on the French Riviera. Instant price estimate, fast confirmation.",
        canonical: "/en/reservation",
      },
    },
    step1: {
      tripTypeHeading: "Trip type",
      oneWay: "One way",
      roundTrip: "Round trip",
      multiTrip: "Multi-stop",
      disposition: "Hourly hire",
      dispositionDuration: "Hourly hire duration",
      minHours: "Minimum 3 hours",
      reduceDuration: "Reduce the duration",
      increaseDuration: "Increase the duration",
      differentReturn: "Different return address",
      tripDetailsHeading: "Trip details",
      departureLabel: "Pick-up location",
      departurePlaceholder: "Enter your address",
      arrivalLabel: "Drop-off location",
      arrivalPlaceholder: "Enter your destination",
      destination: "Destination",
      anywhere: "We'll go wherever you want!",
      dateLabel: "Pick-up date",
      timeLabel: "Pick-up time",
      returnDateLabel: "Return date",
      returnTimeLabel: "Return time",
      returnAddressLabel: "Return address",
      returnAddressPlaceholder: "Enter the return address",
      stepLabel: (n) => `Stop ${n}`,
      stopPlaceholder: "Enter the stop address",
      waitingTime: "Waiting time",
      min: "min",
      removeStop: "Remove the stop",
      addStop: (n, max) => `Add a stop (${n}/${max})`,
      finalArrivalLabel: "Final drop-off location",
      finalArrivalPlaceholder: "Enter your final destination",
      vehiclePassengersHeading: "Vehicle and passengers",
      adults: "Adults",
      adultsAge: "12 years and over",
      reduceAdults: "Reduce the number of adults",
      increaseAdults: "Increase the number of adults",
      showAdvanced: "Show advanced options (children, babies, luggage)",
      hideAdvanced: "Hide advanced options",
      children: "Children",
      childrenAge: "From 3 to 11 years",
      reduceChildren: "Reduce the number of child seats",
      increaseChildren: "Increase the number of child seats",
      babies: "Babies",
      babiesAge: "Under 3 years",
      reduceBabies: "Reduce the number of baby seats",
      increaseBabies: "Increase the number of baby seats",
      luggage: "Luggage",
      luggageDesc: "Standard suitcases",
      reduceLuggage: "Reduce the number of bags",
      increaseLuggage: "Increase the number of bags",
      estimatePrice: "Estimate the price",
      reserve: "Book",
      v: {
        departure: "The pick-up location is required",
        date: "The date is required",
        time: "The time is required",
        arrival: "The drop-off location is required",
        returnDate: "The return date is required",
        returnTime: "The return time is required",
      },
      estimateError:
        "Unable to estimate the price with the current information.",
      priceCalcError:
        "An error occurred while calculating the price. Please try again.",
      reservationError: "An error occurred during the booking",
      unexpectedError: "An unexpected error occurred. Please try again.",
      modalTitle: "No stop added",
      modalBody:
        'You haven\'t added any stops to your trip. Would you like to continue in "one way" mode?',
      cancel: "Cancel",
      continueOneWay: "Continue as one way",
    },
    step2: {
      back: "Back",
      yourInfo: "Your details",
      firstName: "First name",
      firstNamePlaceholder: "Your first name",
      lastName: "Last name",
      lastNamePlaceholder: "Your last name",
      phone: "Phone",
      country: "Country",
      expectedFormat: (example) => `Expected format: ${example}`,
      email: "Email",
      emailPlaceholder: "your@email.com",
      company: "Company (optional)",
      companyPlaceholder: "Your company name",
      sending: "Sending...",
      confirm: "Confirm the booking",
      v: {
        firstNameRequired: "First name is required",
        firstNameMin: "First name must be at least 2 characters",
        lastNameRequired: "Last name is required",
        lastNameMin: "Last name must be at least 2 characters",
        phoneRequired: "Phone number is required",
        phoneInvalid: (example) => `Invalid format. Example: ${example}`,
        emailRequired: "Email is required",
        emailInvalid: "Email is not valid",
      },
    },
    datePicker: {
      today: "Today",
      selectDate: "Select a date",
      prev: "Previous",
      next: "Next",
    },
    timePicker: {
      now: "Now",
      selectTime: "Select a time",
      hours: "Hours",
      minutes: "Minutes",
      hoursTitle: (h) => `${h} hours`,
      minutesTitle: (m) => `${m} minutes`,
    },
    addressInput: {
      stations: "Stations",
      airports: "Airports",
      destinations: "Destinations",
      hotels: "Hotels",
      flightNumber: "Flight number (optional)",
      flightExample: "e.g. AF1234",
      trainNumber: "Train number (optional)",
      trainExample: "e.g. TGV 6214",
    },
    suggestions: {
      gare: "Station",
      aeroport: "Airport",
      destination: "Destination",
      hotel: "Hotel",
      poi: "POI",
      none: "No suggestion found",
    },
    vehicle: {
      select: "Select your vehicle",
      upToPassengers: (n) => `Up to ${n} passengers`,
      upToLuggage: (n) => `Up to ${n} bags`,
      fromPerKm: (p) => `from €${p.toFixed(2)}/km`,
      tooManyPassengers: (a, b) => `Too many passengers (${a} / ${b})`,
      tooManyLuggage: (a, b) => `Too many bags (${a} / ${b})`,
    },
    minimal: {
      title: "Express booking",
      advancedOptions: "Advanced options",
      departureLabel: "Pick-up location",
      departurePlaceholder: "Enter your address",
      arrivalLabel: "Drop-off location",
      arrivalPlaceholder: "Enter your destination",
      dateLabel: "Date",
      timeLabel: "Time",
      reducePassengers: "Reduce the number of passengers",
      increasePassengers: "Increase the number of passengers",
      estimatePrice: "Estimate the price",
      reserve: "Book",
      defaultError: "An error occurred during the booking",
      successTitle: "Booking sent successfully!",
      successReceived: "We have received your booking request.",
      successContactCustom:
        "We will contact you within 48 hours to confirm your tailored journey.",
      successContactStandard:
        "We will contact you in the next few hours to confirm your journey.",
      newReservation: "New booking",
    },
  },
  ar: {
    hero: {
      h1: "حجز راقٍ",
      subtitle:
        "تنقّلات خاصة، ونقل، وخدمة تحت الطلب بالساعة مع كي إس رنت سرفيسز.",
      customizeAria: "تخصيص رحلتك بخيارات إضافية",
    },
    page: {
      crumbHome: "الرئيسية",
      whyHeading: "لماذا الحجز مع كي إس رنت سرفيسز",
      why: [
        {
          title: "إجراء سريع",
          text: "الطلب والتحقّق والتأكيد في خطوات قليلة مع متابعة واضحة.",
        },
        {
          title: "تسعير واضح",
          text: "شروط صريحة قبل التأكيد، دون أي غموض حول الخدمة.",
        },
        {
          title: "فريق محلّي",
          text: "دعم تشغيلي في كان ونيس وموناكو وكامل الريفييرا الفرنسية.",
        },
      ],
      faqHeading: "الأسئلة الشائعة حول الحجز",
      faq: [
        {
          q: "خلال أي مدة أحصل على التأكيد؟",
          a: "يردّ فريقنا بسرعة بتأكيد مفصّل بناءً على طلبك.",
        },
        {
          q: "هل يمكنني تعديل حجزي؟",
          a: "نعم، حسب التوفّر، نكيّف الخدمة وفق قيود توقيتك.",
        },
        {
          q: "هل يمكن الجمع بين السائق والتأجير؟",
          a: "نعم، نوفّر تركيبات سيارة + سائق حسب سياق طلبك.",
        },
      ],
      inspirationHeading: "بحاجة إلى إلهام قبل الحجز؟",
      links: [
        { label: "خدمات السائق الخاص", href: "/ar/chauffeur/services" },
        { label: "رحلات انطلاقًا من كان", href: "/ar/excursions" },
        {
          label: "تأجير سيارات فاخرة",
          href: "/ar/location/vehicules",
        },
      ],
      invoiceSubject: "طلب فاتورة",
      invoiceReviewSubject: "طلب فاتورة + تقييم",
      seo: {
        title: "حجز سائق خاص ونقل سياحي | كي إس رنت سرفيسز",
        description:
          "احجز سائقك الخاص أو نقلك أو خدمتك بالساعة في كان وعلى الريفييرا الفرنسية. تقدير فوري للسعر وتأكيد سريع.",
        canonical: "/ar/reservation",
      },
    },
    step1: {
      tripTypeHeading: "نوع الرحلة",
      oneWay: "ذهاب فقط",
      roundTrip: "ذهاب وإياب",
      multiTrip: "متعدّد المحطات",
      disposition: "خدمة بالساعة",
      dispositionDuration: "مدة الخدمة بالساعة",
      minHours: "الحد الأدنى 3 ساعات",
      reduceDuration: "تقليل المدة",
      increaseDuration: "زيادة المدة",
      differentReturn: "عنوان عودة مختلف",
      tripDetailsHeading: "تفاصيل الرحلة",
      departureLabel: "موقع الانطلاق",
      departurePlaceholder: "أدخل عنوانك",
      arrivalLabel: "موقع الوصول",
      arrivalPlaceholder: "أدخل وجهتك",
      destination: "الوجهة",
      anywhere: "سنذهب إلى حيث تريد!",
      dateLabel: "تاريخ الانطلاق",
      timeLabel: "وقت الانطلاق",
      returnDateLabel: "تاريخ العودة",
      returnTimeLabel: "وقت العودة",
      returnAddressLabel: "عنوان العودة",
      returnAddressPlaceholder: "أدخل عنوان العودة",
      stepLabel: (n) => `المحطة ${n}`,
      stopPlaceholder: "أدخل عنوان المحطة",
      waitingTime: "وقت الانتظار",
      min: "دقيقة",
      removeStop: "حذف المحطة",
      addStop: (n, max) => `إضافة محطة (${n}/${max})`,
      finalArrivalLabel: "موقع الوصول النهائي",
      finalArrivalPlaceholder: "أدخل وجهتك النهائية",
      vehiclePassengersHeading: "السيارة والركاب",
      adults: "بالغون",
      adultsAge: "12 سنة فأكثر",
      reduceAdults: "تقليل عدد البالغين",
      increaseAdults: "زيادة عدد البالغين",
      showAdvanced: "إظهار الخيارات المتقدّمة (أطفال، رُضّع، أمتعة)",
      hideAdvanced: "إخفاء الخيارات المتقدّمة",
      children: "أطفال",
      childrenAge: "من 3 إلى 11 سنة",
      reduceChildren: "تقليل عدد مقاعد الأطفال",
      increaseChildren: "زيادة عدد مقاعد الأطفال",
      babies: "رُضّع",
      babiesAge: "أقل من 3 سنوات",
      reduceBabies: "تقليل عدد مقاعد الرُّضّع",
      increaseBabies: "زيادة عدد مقاعد الرُّضّع",
      luggage: "الأمتعة",
      luggageDesc: "حقائب قياسية",
      reduceLuggage: "تقليل عدد الحقائب",
      increaseLuggage: "زيادة عدد الحقائب",
      estimatePrice: "تقدير السعر",
      reserve: "احجز",
      v: {
        departure: "موقع الانطلاق مطلوب",
        date: "التاريخ مطلوب",
        time: "الوقت مطلوب",
        arrival: "موقع الوصول مطلوب",
        returnDate: "تاريخ العودة مطلوب",
        returnTime: "وقت العودة مطلوب",
      },
      estimateError: "تعذّر تقدير السعر بالمعلومات الحالية.",
      priceCalcError: "حدث خطأ أثناء حساب السعر. يرجى المحاولة مرة أخرى.",
      reservationError: "حدث خطأ أثناء الحجز",
      unexpectedError: "حدث خطأ غير متوقّع. يرجى المحاولة مرة أخرى.",
      modalTitle: "لم تتم إضافة أي محطة",
      modalBody:
        'لم تُضِف أي محطات إلى رحلتك. هل ترغب في المتابعة بنمط "الذهاب فقط"؟',
      cancel: "إلغاء",
      continueOneWay: "المتابعة كذهاب فقط",
    },
    step2: {
      back: "رجوع",
      yourInfo: "معلوماتك",
      firstName: "الاسم الأول",
      firstNamePlaceholder: "اسمك الأول",
      lastName: "اسم العائلة",
      lastNamePlaceholder: "اسم عائلتك",
      phone: "الهاتف",
      country: "البلد",
      expectedFormat: (example) => `الصيغة المتوقّعة: ${example}`,
      email: "البريد الإلكتروني",
      emailPlaceholder: "your@email.com",
      company: "الشركة (اختياري)",
      companyPlaceholder: "اسم شركتك",
      sending: "جارٍ الإرسال...",
      confirm: "تأكيد الحجز",
      v: {
        firstNameRequired: "الاسم الأول مطلوب",
        firstNameMin: "يجب أن يتكوّن الاسم الأول من حرفين على الأقل",
        lastNameRequired: "اسم العائلة مطلوب",
        lastNameMin: "يجب أن يتكوّن اسم العائلة من حرفين على الأقل",
        phoneRequired: "رقم الهاتف مطلوب",
        phoneInvalid: (example) => `صيغة غير صحيحة. مثال: ${example}`,
        emailRequired: "البريد الإلكتروني مطلوب",
        emailInvalid: "البريد الإلكتروني غير صحيح",
      },
    },
    datePicker: {
      today: "اليوم",
      selectDate: "اختر تاريخًا",
      prev: "السابق",
      next: "التالي",
    },
    timePicker: {
      now: "الآن",
      selectTime: "اختر وقتًا",
      hours: "الساعات",
      minutes: "الدقائق",
      hoursTitle: (h) => `${h} ساعة`,
      minutesTitle: (m) => `${m} دقيقة`,
    },
    addressInput: {
      stations: "محطات القطار",
      airports: "المطارات",
      destinations: "الوجهات",
      hotels: "الفنادق",
      flightNumber: "رقم الرحلة الجوية (اختياري)",
      flightExample: "مثال: AF1234",
      trainNumber: "رقم القطار (اختياري)",
      trainExample: "مثال: TGV 6214",
    },
    suggestions: {
      gare: "محطة قطار",
      aeroport: "مطار",
      destination: "وجهة",
      hotel: "فندق",
      poi: "نقطة اهتمام",
      none: "لم يُعثر على أي اقتراح",
    },
    vehicle: {
      select: "اختر سيارتك",
      upToPassengers: (n) => `حتى ${n} ركاب`,
      upToLuggage: (n) => `حتى ${n} حقائب`,
      fromPerKm: (p) => `من ${p.toFixed(2)} يورو/كم`,
      tooManyPassengers: (a, b) => `عدد الركاب كبير جدًا (${a} / ${b})`,
      tooManyLuggage: (a, b) => `عدد الحقائب كبير جدًا (${a} / ${b})`,
    },
    minimal: {
      title: "حجز سريع",
      advancedOptions: "خيارات متقدّمة",
      departureLabel: "موقع الانطلاق",
      departurePlaceholder: "أدخل عنوانك",
      arrivalLabel: "موقع الوصول",
      arrivalPlaceholder: "أدخل وجهتك",
      dateLabel: "التاريخ",
      timeLabel: "الوقت",
      reducePassengers: "تقليل عدد الركاب",
      increasePassengers: "زيادة عدد الركاب",
      estimatePrice: "تقدير السعر",
      reserve: "احجز",
      defaultError: "حدث خطأ أثناء الحجز",
      successTitle: "تم إرسال الحجز بنجاح!",
      successReceived: "لقد استلمنا طلب حجزك.",
      successContactCustom: "سنتواصل معك خلال 48 ساعة لتأكيد رحلتك المخصّصة.",
      successContactStandard: "سنتواصل معك خلال الساعات القادمة لتأكيد رحلتك.",
      newReservation: "حجز جديد",
    },
  },
};
